import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { dayCellsForMonth } from '~/shared/dates';
import { densityClass as classifyDensity } from '~/shared/density';
import type { Avail, Density, DensityClass } from '~/shared/types';

import { Icon } from './Icons';

const DOW = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const HOVER_DELAY_MS = 600;
const TOOLTIP_GAP = 10;

function monthKey(year: number, month: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}`;
}

interface HoverState {
  iso: string;
  rect: { left: number; right: number; top: number; bottom: number };
}

export type AvailMap = Map<string, Avail>;

interface CalendarProps {
  year: number;
  month: number;
  marks?: AvailMap;
  onChangeMarks?: (next: AvailMap) => void;
  density?: Density;
  total?: number;
  mode?: 'select' | 'view';
  triState?: boolean;
  monthNav?: boolean;
  onMonthChange?: (year: number, month: number) => void;
  todayIso?: string | null;
  windowStart?: string;
  windowEnd?: string;
  onCellClick?: (iso: string) => void;
  renderCellHover?: (iso: string) => ReactNode;
}

function nextInCycle(current: Avail | undefined, triState: boolean): Avail | undefined {
  if (!triState) {
    return current === 'yes' ? undefined : 'yes';
  }
  if (current === undefined) return 'yes';
  if (current === 'yes') return 'maybe';
  return undefined;
}

export function Calendar({
  year,
  month,
  marks,
  onChangeMarks,
  density = {},
  total = 1,
  mode = 'select',
  triState = false,
  monthNav = true,
  onMonthChange,
  todayIso = null,
  windowStart,
  windowEnd,
  onCellClick,
  renderCellHover,
}: CalendarProps) {
  const cells = dayCellsForMonth(year, month);
  const marksRef = useRef<AvailMap>(marks ?? new Map());
  marksRef.current = marks ?? new Map();

  const dragMode = useRef<'add-yes' | 'clear' | null>(null);
  const dragSeen = useRef<Set<string>>(new Set());
  const dragMoved = useRef(false);
  const dragInitialIso = useRef<string | null>(null);

  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [hover, setHover] = useState<HoverState | null>(null);

  const isOutsideWindow = (iso: string): boolean => {
    if (windowStart && iso < windowStart) return true;
    if (windowEnd && iso > windowEnd) return true;
    return false;
  };

  const densityFor = (iso: string): DensityClass => {
    if (mode !== 'view') return 0;
    return classifyDensity(density[iso] ?? { yes: 0, maybe: 0 }, total);
  };

  const setMark = (iso: string, value: Avail | undefined): void => {
    if (mode !== 'select' || !onChangeMarks || isOutsideWindow(iso)) return;
    const next = new Map(marksRef.current);
    if (value === undefined) next.delete(iso);
    else next.set(iso, value);
    marksRef.current = next;
    onChangeMarks(next);
  };

  useEffect(() => {
    function handleMove(e: PointerEvent) {
      if (!dragMode.current) return;
      const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
      if (!el) return;
      const cell = el.closest<HTMLElement>('[data-iso]');
      if (!cell) return;
      const iso = cell.dataset.iso;
      if (!iso || dragSeen.current.has(iso)) return;
      // First crossed cell after pointerdown: treat as drag, apply to the
      // initial cell too so the drag-start point isn't ignored.
      if (!dragMoved.current && dragInitialIso.current) {
        dragMoved.current = true;
        const initial = dragInitialIso.current;
        if (dragMode.current === 'add-yes') {
          setMark(initial, 'yes');
        } else {
          setMark(initial, undefined);
        }
      }
      dragSeen.current.add(iso);
      if (dragMode.current === 'add-yes') setMark(iso, 'yes');
      else setMark(iso, undefined);
    }
    function handleEnd() {
      // Tap (no drag motion): cycle the initial cell instead of bulk action.
      if (dragMode.current && !dragMoved.current && dragInitialIso.current) {
        const iso = dragInitialIso.current;
        const current = marksRef.current.get(iso);
        const next = nextInCycle(current, triState);
        setMark(iso, next);
      }
      dragMode.current = null;
      dragMoved.current = false;
      dragInitialIso.current = null;
      dragSeen.current = new Set();
    }
    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleEnd);
    document.addEventListener('pointercancel', handleEnd);
    return () => {
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleEnd);
      document.removeEventListener('pointercancel', handleEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, onChangeMarks, windowStart, windowEnd, triState]);

  useEffect(() => {
    return () => {
      if (hoverTimer.current) clearTimeout(hoverTimer.current);
    };
  }, []);

  const onPointerDown = (iso: string) => (e: React.PointerEvent<HTMLDivElement>) => {
    if (mode !== 'select' || isOutsideWindow(iso)) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    e.preventDefault();
    const current = marksRef.current.get(iso);
    // Drag policy: starting from a yes cell drags to clear; starting from
    // empty or maybe, drag adds yes. The initial cell is not modified yet —
    // pointerup decides whether it was a tap (cycle) or drag (bulk).
    dragMode.current = current === 'yes' ? 'clear' : 'add-yes';
    dragMoved.current = false;
    dragInitialIso.current = iso;
    dragSeen.current = new Set([iso]);
  };

  const cancelHover = () => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
    setHover(null);
  };

  const startHover = (iso: string, target: HTMLElement) => {
    if (!renderCellHover || mode !== 'view' || isOutsideWindow(iso)) return;
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => {
      const r = target.getBoundingClientRect();
      setHover({
        iso,
        rect: { left: r.left, right: r.right, top: r.top, bottom: r.bottom },
      });
    }, HOVER_DELAY_MS);
  };

  const prev = () => {
    const m = month === 0 ? 11 : month - 1;
    const y = month === 0 ? year - 1 : year;
    onMonthChange?.(y, m);
  };
  const next = () => {
    const m = month === 11 ? 0 : month + 1;
    const y = month === 11 ? year + 1 : year;
    onMonthChange?.(y, m);
  };

  const current = monthKey(year, month);
  const canPrev = !windowStart || current > windowStart.slice(0, 7);
  const canNext = !windowEnd || current < windowEnd.slice(0, 7);

  return (
    <div className="cal-wrap">
      <div className="cal-month-title">
        <span>
          {MONTHS[month]} {year}
        </span>
        {monthNav && (
          <div className="row" style={{ gap: 4 }}>
            <button
              className="btn btn-ghost btn-sm"
              onClick={prev}
              disabled={!canPrev}
              aria-label="previous month"
              type="button"
            >
              <Icon name="arrow-l" size={14} />
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={next}
              disabled={!canNext}
              aria-label="next month"
              type="button"
            >
              <Icon name="arrow" size={14} />
            </button>
          </div>
        )}
      </div>
      <div className="cal-grid">
        {DOW.map((d) => (
          <div key={d} className="cal-dow">
            {d}
          </div>
        ))}
        {cells.map((iso, i) => {
          if (!iso) return <div key={`empty-${i}`} className="cal-cell empty" />;
          const outside = isOutsideWindow(iso);
          const cls = ['cal-cell'];
          const mark = marks?.get(iso);
          if (mode === 'view') {
            const c = densityFor(iso);
            cls.push(`density-${c}`);
          }
          if (mode === 'select' && mark === 'yes') {
            cls.push('density-5', 'mark-yes');
          }
          if (mode === 'select' && mark === 'maybe') {
            cls.push('mark-maybe');
          }
          if (iso === todayIso) cls.push('today');
          if (outside) cls.push('disabled');
          const dayDensity = density[iso];
          const visibleCount = dayDensity ? dayDensity.yes + dayDensity.maybe : 0;
          const dayNum = Number(iso.slice(8, 10));
          const isClickable = mode === 'view' && !outside && onCellClick !== undefined;
          return (
            <div
              key={iso}
              data-iso={iso}
              className={cls.join(' ')}
              onPointerDown={mode === 'select' ? onPointerDown(iso) : undefined}
              onPointerEnter={(e) => {
                if (e.pointerType === 'touch') return;
                startHover(iso, e.currentTarget);
              }}
              onPointerLeave={cancelHover}
              onClick={
                isClickable
                  ? () => {
                      cancelHover();
                      onCellClick(iso);
                    }
                  : undefined
              }
              role={mode === 'select' || isClickable ? 'button' : undefined}
              aria-pressed={mode === 'select' ? mark === 'yes' : undefined}
              aria-disabled={outside || undefined}
              tabIndex={isClickable ? 0 : undefined}
              onKeyDown={
                isClickable
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onCellClick(iso);
                      }
                    }
                  : undefined
              }
            >
              <div className="num">{dayNum}</div>
              {mode === 'view' && visibleCount > 0 && (
                <div className="count">
                  {dayDensity?.yes ?? 0}
                  {dayDensity?.maybe ? `+${dayDensity.maybe}` : ''}/{total}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {hover && renderCellHover && (
        <CellHoverTooltip hover={hover}>{renderCellHover(hover.iso)}</CellHoverTooltip>
      )}
    </div>
  );
}

interface CellHoverTooltipProps {
  hover: HoverState;
  children: ReactNode;
}

function CellHoverTooltip({ hover, children }: CellHoverTooltipProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{
    left: number;
    top: number;
    placement: 'top' | 'bottom';
  } | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    const cellCenterX = (hover.rect.left + hover.rect.right) / 2;
    const above = hover.rect.top - TOOLTIP_GAP - rect.height;
    const below = hover.rect.bottom + TOOLTIP_GAP;
    const fitsAbove = above >= 8;
    const fitsBelow = below + rect.height <= viewportH - 8;
    const placement = fitsAbove || !fitsBelow ? 'top' : 'bottom';
    const top = placement === 'top' ? above : below;

    let left = cellCenterX - rect.width / 2;
    if (left < 8) left = 8;
    if (left + rect.width > viewportW - 8) left = viewportW - 8 - rect.width;

    setPosition({ left, top, placement });
  }, [hover]);

  return createPortal(
    <div
      ref={ref}
      className="cell-tooltip"
      style={{
        position: 'fixed',
        left: position?.left ?? -9999,
        top: position?.top ?? -9999,
        opacity: position ? 1 : 0,
        pointerEvents: 'none',
        zIndex: 100,
      }}
      data-placement={position?.placement ?? 'top'}
      role="tooltip"
    >
      {children}
    </div>,
    document.body,
  );
}
